import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import Reveal from 'reveal.js';
import { DEFAULTS } from '../../../constants/Constants';
import { BaseWidget, DynamicObject } from '../layout/View';
import { SourceItem } from './slideshow/types';
import { evaluateSource, parseUrls } from './slideshow/utils';
import ImageSlide from './slideshow/ImageSlide';
import VideoSlide from './slideshow/VideoSlide';
import PDFSlide from './slideshow/PDFSlide';
import WidgetSlide from './slideshow/WidgetSlide';
import { createComponentLogger } from './slideshow/logger';
import { IWidget } from './Widget';

export type SlideshowWidget = BaseWidget & {
  type: 'slideshow';
  slideshow?: {
    interval?: number; // Re-evaluation interval for JavaScript code (in seconds)
    duration?: number; // Default slide duration (in seconds)
    source?: string; // Newline-separated URLs or JavaScript code
    isJavaScript?: boolean; // Whether source is JavaScript code
    shuffle?: boolean; // Whether to shuffle the sources after evaluation
    afterReevaluate?: boolean; // Re-evaluate after the last slide is shown
    widgets?: DynamicObject<IWidget>; // Dashboard widgets for rendering widget slides
    // Deprecated, kept for backward compatibility
    images?: string[];
  };
};

const Slideshow: React.FC<{ options: SlideshowWidget['slideshow'] }> = ({ options }) => {
  const id = useId();
  const revealRef = useRef<Reveal.Api>();
  const [sources, setSources] = useState<SourceItem[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [slideIteration, setSlideIteration] = useState(0);
  const [isRevealInitialized, setIsRevealInitialized] = useState(false);
  const currentSlideIndexRef = useRef(0);
  const sourcesRef = useRef<SourceItem[]>([]);
  const sourcesLengthRef = useRef(0);
  const isTransitioningRef = useRef(false);

  // Create logger instance once
  const log = useMemo(() => createComponentLogger('Slideshow'), []);

  // Keep refs in sync with state
  useEffect(() => {
    currentSlideIndexRef.current = currentSlideIndex;
    log('📍 Current slide index updated', { currentSlideIndex });
  }, [currentSlideIndex, log]);

  useEffect(() => {
    sourcesRef.current = sources;
    sourcesLengthRef.current = sources.length;
    log('📚 Sources updated', { count: sources.length });
  }, [sources, log]);

  // Shuffle array helper (Fisher-Yates algorithm)
  const shuffleArray = useCallback(<T, >(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  // Get sources based on configuration
  const getSources = useCallback(async (): Promise<SourceItem[]> => {
    log('🔍 Getting sources', {
      hasSource: !!options?.source,
      hasImages: !!options?.images,
      isJavaScript: options?.isJavaScript
    });

    // Support backward compatibility with old 'images' field
    const sourceText = options?.source ?? options?.images?.join('\n') ?? '';
    const isJavaScript = options?.isJavaScript ?? false;

    if (sourceText.length === 0) {
      log('⚠️  No source text provided');
      return [];
    }

    if (isJavaScript) {
      log('⚙️  Evaluating JavaScript source');
      return await evaluateSource(sourceText);
    } else {
      log('📝 Parsing URLs from source');
      return parseUrls(sourceText);
    }
  }, [options?.images, options?.isJavaScript, options?.source, log]);

  // Update sources and optionally shuffle
  // Returns true if sources were updated, false if unchanged
  const updateSources = useCallback(async (): Promise<boolean> => {
    log('🔄 Updating sources');
    let newSources = await getSources();

    // Apply shuffle if enabled
    if (options?.shuffle) {
      log('🔀 Shuffling sources');
      newSources = shuffleArray(newSources);
    }

    // CRITICAL: Access current sources from ref to avoid stale closure
    // This ensures we always compare against the actual current state
    const currentSources = sourcesRef.current;

    // Check if sources changed synchronously before setState
    let sourcesChanged = false;

    if (currentSources.length !== newSources.length || currentSources.length === 0) {
      log('✨ Sources changed (length differs)', {
        oldCount: currentSources.length,
        newCount: newSources.length
      });
      sourcesChanged = true;
    } else {
      // Deep comparison only if lengths match
      const contentChanged = JSON.stringify(currentSources) !== JSON.stringify(newSources);
      if (contentChanged) {
        log('✨ Source content changed');
        sourcesChanged = true;
      } else {
        log('✓ Sources unchanged');
        sourcesChanged = false;
      }
    }

    // Now update state based on what we determined
    if (sourcesChanged) {
      log('📝 Applying new sources and resetting to first slide');
      setSources(newSources);
      setCurrentSlideIndex(0);
      currentSlideIndexRef.current = 0;
      // Reset iteration counter when sources change
      setSlideIteration(0);
    }

    return sourcesChanged;
  }, [getSources, options?.shuffle, shuffleArray, log]);

  // Handle slide completion - CRITICAL: No currentSlideIndex in dependencies!
  const handleSlideComplete = useCallback(async () => {
    if (isTransitioningRef.current) {
      log('⚠️  Transition already in progress, ignoring completion');
      return;
    }

    const currentIndex = currentSlideIndexRef.current;
    const totalSlides = sourcesLengthRef.current;

    log('🎬 Slide complete', {
      currentIndex,
      totalSlides,
      isLastSlide: currentIndex === totalSlides - 1
    });

    isTransitioningRef.current = true;

    const isLastSlide = currentIndex === totalSlides - 1;

    if (isLastSlide && options?.afterReevaluate && options?.isJavaScript) {
      log('🔄 Last slide + re-evaluate enabled, fetching new sources');
      const sourcesChanged = await updateSources();

      // If sources didn't change, we still need to loop back to first slide
      if (!sourcesChanged) {
        log('🔁 Sources unchanged after re-evaluation, looping to first slide');
        setCurrentSlideIndex(0);
        currentSlideIndexRef.current = 0;
        // Increment iteration to force remount even when going from index 0 to 0
        setSlideIteration(prev => prev + 1);
      }
      // If sources changed, updateSources already reset to slide 0
    } else if (isLastSlide) {
      log('🔁 Last slide reached, looping to first');
      setCurrentSlideIndex(0);
      currentSlideIndexRef.current = 0;
      // Increment iteration to force remount even when going from index 0 to 0
      setSlideIteration(prev => prev + 1);
    } else {
      const nextIndex = currentIndex + 1;
      log('➡️  Advancing to next slide', { nextIndex });
      setCurrentSlideIndex(nextIndex);
      currentSlideIndexRef.current = nextIndex;
    }

    // Use requestAnimationFrame to ensure state update completes before allowing next transition
    requestAnimationFrame(() => {
      isTransitioningRef.current = false;
      log('✅ Transition complete, ready for next');
    });
  }, [options?.afterReevaluate, options?.isJavaScript, updateSources, log]);

  // Handle media errors - skip to next slide
  const handleMediaError = useCallback((error: unknown) => {
    log('❌ Media error, skipping to next slide', {
      error: error instanceof Error ? error.message : String(error),
      currentIndex: currentSlideIndexRef.current
    });
    handleSlideComplete();
  }, [handleSlideComplete, log]);

  // Initial load and re-evaluation interval
  useEffect(() => {
    log('🚀 Slideshow mounted, loading initial sources');
    updateSources();

    // Set up re-evaluation interval if in JavaScript mode and interval > 0
    if (options?.isJavaScript && options?.interval && options.interval > 0) {
      log('⏰ Setting up re-evaluation interval', { intervalSeconds: options.interval });
      const intervalId = setInterval(() => {
        log('🔄 Re-evaluation interval triggered');
        updateSources();
      }, options.interval * 1000);

      return () => {
        log('🧹 Clearing re-evaluation interval');
        clearInterval(intervalId);
      };
    }
  }, [options?.source, options?.isJavaScript, options?.interval, options?.shuffle, options?.images, updateSources, log]);

  // Initialize/configure Reveal.js (simplified - no autoSlide or loop)
  useEffect(() => {
    if (sources.length === 0) {
      log('⏸️  No sources available, skipping Reveal.js initialization');
      return;
    }

    const revealOptions: Reveal.Options = {
      width: '100%',
      height: '100%',
      margin: 0.0,
      transition: 'fade',
      embedded: true,
      controls: false,
      progress: false,
      keyboard: false,
    };

    if (revealRef.current == null) {
      log('🎭 Initializing Reveal.js', { sourcesCount: sources.length });
      const deck = document.getElementById(id);
      if (deck) {
        revealRef.current = new Reveal(deck, revealOptions);
        revealRef.current.initialize().then(() => {
          log('✅ Reveal.js initialized successfully');
          setIsRevealInitialized(true);
        });
      } else {
        log('❌ Could not find Reveal.js deck element', { id });
      }
    } else {
      log('🔄 Syncing Reveal.js with updated sources', { sourcesCount: sources.length, slideIteration });
      // Sync to update slides after sources change or iteration changes
      // Use requestAnimationFrame to ensure DOM updates are complete before syncing
      requestAnimationFrame(() => {
        if (revealRef.current) {
          revealRef.current.sync();
          log('✅ Reveal.js sync complete');
        }
      });
    }
  }, [id, sources, slideIteration, log]);

  // Update reveal.js slide position when currentSlideIndex changes
  useEffect(() => {
    if (revealRef.current && sources.length > 0 && isRevealInitialized) {
      log('🎯 Navigating Reveal.js to slide', {
        slideIndex: currentSlideIndex,
        totalSlides: sources.length,
        slideIteration
      });
      // Use requestAnimationFrame to ensure any pending syncs are complete
      requestAnimationFrame(() => {
        if (revealRef.current) {
          revealRef.current.slide(currentSlideIndex);
          log('✅ Navigation complete');
        }
      });
    }
  }, [currentSlideIndex, sources.length, isRevealInitialized, slideIteration, log]);

  const defaultDuration = options?.duration ?? DEFAULTS.WIDGET_SLIDESHOW_INTERVAL;

  // Log render info
  if (sources.length > 0) {
    log('🎨 Rendering slideshow', {
      sourcesCount: sources.length,
      currentSlideIndex,
      defaultDuration,
      isRevealInitialized
    });
  }

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
      }}
    >
      <style>
        {`
          .reveal .slides section[data-widget-slide="true"] {
            display: block !important;
            text-align: left !important;
            align-items: stretch !important;
            justify-content: stretch !important;
            padding: 0 !important;
          }
          .reveal .slides section[data-widget-slide="true"] > * {
            width: 100% !important;
            height: 100% !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
          }
        `}
      </style>
      <div className="reveal" id={id}>
        <div className="slides">
          {sources.map((source, i) => {
            const mediaType = source.mediaType ?? 'image';
            const isActive = i === currentSlideIndex;
            // Use source URL + index + iteration as key to ensure proper component identity
            // The iteration counter forces remount when looping (especially important for single-slide shows)
            const slideKey = `${mediaType}-${i}-${source.url}-${slideIteration}`;

            // Style for sections - widget slides get special treatment via CSS and data attribute
            const sectionStyle = { width: '100%', height: '100%' };

            return (
              <section key={slideKey} style={sectionStyle} data-widget-slide={mediaType === 'widget'}>
                {mediaType === 'video' ? (
                  <VideoSlide
                    source={source}
                    isActive={isActive}
                    defaultDuration={defaultDuration}
                    onComplete={handleSlideComplete}
                    onError={handleMediaError}
                  />
                ) : mediaType === 'pdf' ? (
                  <PDFSlide
                    source={source}
                    isActive={isActive}
                    defaultDuration={defaultDuration}
                    onComplete={handleSlideComplete}
                    onError={handleMediaError}
                  />
                ) : mediaType === 'widget' ? (
                  <WidgetSlide
                    source={source}
                    isActive={isActive}
                    defaultDuration={defaultDuration}
                    onComplete={handleSlideComplete}
                    onError={handleMediaError}
                    widgets={options?.widgets ?? {}}
                  />
                ) : (
                  <ImageSlide
                    source={source}
                    isActive={isActive}
                    defaultDuration={defaultDuration}
                    onComplete={handleSlideComplete}
                    onError={handleMediaError}
                  />
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Slideshow;
